import { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  CalendarDaysIcon, ClockIcon, UserCircleIcon,
  ArrowRightOnRectangleIcon, ArrowLeftOnRectangleIcon,
  BanknotesIcon, FunnelIcon, CheckCircleIcon,
  XCircleIcon, ExclamationCircleIcon,
} from '@heroicons/react/24/outline';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .att-root { font-family:'Sora',sans-serif; color:#1a1d2e; }

  .att-page-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:1.75rem; flex-wrap:wrap; gap:.5rem; }
  .att-page-title  { font-size:1.6rem; font-weight:700; letter-spacing:-.03em; color:#0f1123; }
  .att-page-sub    { font-size:.78rem; color:#9094a8; margin-top:2px; }

  .att-panel { background:white; border-radius:16px; border:1px solid #e8ebf4; margin-bottom:1.25rem; overflow:hidden; }
  .att-panel-header { display:flex; align-items:center; gap:10px; padding:16px 20px; border-bottom:1px solid #f0f2f8; }
  .att-panel-icon { width:34px; height:34px; border-radius:10px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .att-panel-icon svg { width:17px; height:17px; stroke-width:1.8; }
  .att-panel-icon.blue   { background:#eef2ff; color:#3b6ef8; }
  .att-panel-icon.green  { background:#ecfdf5; color:#10c98e; }
  .att-panel-icon.purple { background:#f3f0ff; color:#8b5cf6; }
  .att-panel-title { font-size:.88rem; font-weight:600; color:#1a1d2e; }
  .att-panel-sub   { font-size:.72rem; color:#9094a8; margin-top:1px; }
  .att-panel-body  { padding:18px 20px; }

  .filter-row { display:flex; flex-wrap:wrap; gap:14px; align-items:flex-end; }
  .filter-field { display:flex; flex-direction:column; gap:5px; }
  .filter-label { font-size:.7rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .filter-input,
  .filter-select,
  .react-datepicker-wrapper input {
    font-family:'Sora',sans-serif !important; font-size:.82rem !important;
    padding:8px 12px !important; border:1px solid #e0e4f0 !important;
    border-radius:10px !important; background:#f8f9fc !important;
    color:#1a1d2e !important; outline:none !important;
    transition:border-color .15s,background .15s !important; min-width:140px;
  }
  .filter-input:focus, .filter-select:focus, .react-datepicker-wrapper input:focus {
    border-color:#3b6ef8 !important; background:white !important;
  }

  .role-toggle { display:flex; background:#f0f2f7; border-radius:10px; padding:3px; gap:2px; }
  .role-btn { padding:6px 16px; border-radius:8px; font-size:.78rem; font-weight:600; font-family:'Sora',sans-serif; border:none; cursor:pointer; transition:background .18s,color .18s,box-shadow .18s; color:#7880a4; background:transparent; }
  .role-btn.active { background:white; color:#1a1d2e; box-shadow:0 1px 4px rgba(0,0,0,.1); }

  .btn { padding:8px 18px; border-radius:10px; font-size:.8rem; font-weight:600; font-family:'Sora',sans-serif; cursor:pointer; border:none; display:inline-flex; align-items:center; gap:7px; transition:transform .15s,box-shadow .15s; }
  .btn:active { transform:scale(.97); }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-blue   { background:#3b6ef8; color:white; }
  .btn-blue:hover   { box-shadow:0 4px 14px rgba(59,110,248,.35); }
  .btn-green  { background:#10c98e; color:white; }
  .btn-green:hover  { box-shadow:0 4px 14px rgba(16,201,142,.35); }
  .btn-amber  { background:#f59e0b; color:white; }
  .btn-amber:hover  { box-shadow:0 4px 14px rgba(245,158,11,.35); }
  .btn-purple { background:#8b5cf6; color:white; }
  .btn-purple:hover { box-shadow:0 4px 14px rgba(139,92,246,.35); }

  .checkin-grid { display:flex; flex-wrap:wrap; gap:14px; align-items:flex-end; }

  .att-table-wrap { overflow-x:auto; -webkit-overflow-scrolling:touch; }
  .att-table { width:100%; border-collapse:collapse; font-size:.8rem; }
  .att-table thead tr { background:#f8f9fc; border-bottom:1px solid #eaecf4; }
  .att-table th { padding:11px 16px; text-align:center !important; font-size:.67rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .att-table tbody tr { border-bottom:1px solid #f3f5fb; transition:background .12s; }
  .att-table tbody tr:last-child { border-bottom:none; }
  .att-table tbody tr:hover { background:#fafbff; }
  .att-table td { padding:11px 16px; white-space:nowrap; color:#3a3f58; font-size:.8rem; text-align:center !important; }
  .att-table .mono { font-family:'JetBrains Mono',monospace; font-size:.75rem; color:#6b7194; }

  .badge { display:inline-flex; align-items:center; gap:4px; padding:3px 9px; border-radius:99px; font-size:.7rem; font-weight:600; text-transform:capitalize; }
  .badge svg { width:11px; height:11px; stroke-width:2.5; }
  .badge-present { background:#ecfdf5; color:#10c98e; }
  .badge-absent  { background:#fff1f1; color:#ef4444; }
  .badge-late    { background:#fffbeb; color:#f59e0b; }
  .badge-default { background:#f0f2f7; color:#6b7194; }

  .att-empty { text-align:center; padding:48px 20px; color:#b0b5cc; font-size:.82rem; }
  .att-empty-icon { width:40px; height:40px; margin:0 auto 12px; opacity:.3; }

  /* ── Mobile cards ── */
  .att-cards { display:none; padding:10px; }
  .att-card  { background:#fafbff; border-radius:12px; border:1px solid #e8ebf4; padding:12px 13px; margin-bottom:8px; }
  .att-card-top { display:flex; align-items:center; justify-content:space-between; margin-bottom:8px; gap:8px; }
  .att-card-row { display:flex; justify-content:space-between; align-items:center; font-size:.76rem; padding:4px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .att-card-row:last-child { border-bottom:none; }
  .att-card-lbl { color:#9094a8; font-weight:600; white-space:nowrap; }

  .salary-card { margin-top:16px; background:#f8f9fc; border:1px solid #e8ebf4; border-radius:14px; padding:18px 20px; display:grid; grid-template-columns:repeat(auto-fill,minmax(180px,1fr)); gap:14px; }
  .salary-item { display:flex; flex-direction:column; gap:3px; }
  .salary-item-label { font-size:.67rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; }
  .salary-item-value { font-family:'JetBrains Mono',monospace; font-size:.92rem; font-weight:500; color:#1a1d2e; }
  .salary-item-value.highlight { font-size:1.1rem; color:#10c98e; font-weight:700; }
  .salary-item-value.danger { color:#ef4444; }
  .salary-divider { grid-column:1/-1; height:1px; background:#e8ebf4; }

  .user-cell { display:flex; align-items:center; gap:9px; }
  .user-avatar-sm { width:28px; height:28px; border-radius:8px; background:linear-gradient(135deg,#3b6ef8,#8b5cf6); display:flex; align-items:center; justify-content:center; font-size:.65rem; font-weight:700; color:white; flex-shrink:0; }

  .react-datepicker { font-family:'Sora',sans-serif !important; border:1px solid #e0e4f0 !important; border-radius:12px !important; box-shadow:0 8px 24px rgba(0,0,0,.1) !important; overflow:hidden; }
  .react-datepicker__header { background:#f8f9fc !important; border-bottom:1px solid #eaecf4 !important; }
  .react-datepicker__current-month { font-family:'Sora',sans-serif !important; font-weight:600 !important; color:#1a1d2e !important; font-size:.82rem !important; }
  .react-datepicker__day--selected { background:#3b6ef8 !important; border-radius:8px !important; }
  .react-datepicker__day:hover { border-radius:8px !important; }
  .react-datepicker-popper { z-index:100 !important; }

  .att-fade { animation:attFadeUp .4s ease both; }
  @keyframes attFadeUp { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }

  /* ── Mobile ── */
  @media (max-width:768px) {
    .att-page-title { font-size:1.2rem; }
    .att-page-sub   { font-size:.72rem; }
    .att-page-header { margin-bottom:1rem; }

    .att-panel-header { padding:12px 14px; }
    .att-panel-body   { padding:14px; }

    /* Stack filter fields full width */
    .filter-row { flex-direction:column; gap:10px; }
    .filter-field { width:100%; }
    .filter-input,
    .filter-select,
    .react-datepicker-wrapper,
    .react-datepicker-wrapper input {
      width:100% !important; min-width:unset !important;
    }
    .role-toggle { width:fit-content; }
    .btn { width:100%; justify-content:center; }

    /* Checkin grid stack */
    .checkin-grid { flex-direction:column; }
    .checkin-grid .filter-field { width:100%; }
    .checkin-grid .btn { width:100%; justify-content:center; }

    /* Salary card 1 col */
    .salary-card { grid-template-columns:1fr 1fr; }

    .att-table-wrap { display:none !important; }
    .att-cards { display:block; }
  }

  @media (max-width:480px) {
    .salary-card { grid-template-columns:1fr; }
  }
`;

const getInitials = (name = '') =>
  name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';

const StatusBadge = ({ status }) => {
  const s = status?.toLowerCase();
  if (s === 'present') return <span className="badge badge-present"><CheckCircleIcon style={{display:'inline',width:11,height:11}} />Present</span>;
  if (s === 'absent')  return <span className="badge badge-absent"><XCircleIcon style={{display:'inline',width:11,height:11}} />Absent</span>;
  if (s === 'late')    return <span className="badge badge-late"><ExclamationCircleIcon style={{display:'inline',width:11,height:11}} />Late</span>;
  return <span className="badge badge-default">{status || '—'}</span>;
};

const Attendance = () => {
  const [startDate,        setStartDate]        = useState(new Date(new Date().setDate(1)));
  const [endDate,          setEndDate]          = useState(new Date());
  const [role,             setRole]             = useState('student');
  const [attendances,      setAttendances]      = useState([]);
  const [teachers,         setTeachers]         = useState([]);
  const [selectedTeacher,  setSelectedTeacher]  = useState('');
  const [salaryMonth,      setSalaryMonth]      = useState(new Date().toISOString().slice(0,7));
  const [salaryResult,     setSalaryResult]     = useState(null);

  useEffect(() => { fetchAttendance(); fetchTeachers(); }, [startDate, endDate, role]);

  const fetchAttendance = async () => {
    try {
      const params = new URLSearchParams({ startDate: startDate.toISOString(), endDate: endDate.toISOString(), role });
      const { data } = await api.get(`/admin/attendance/overview?${params}`);
      setAttendances(data);
    } catch { toast.error('Failed to fetch attendance'); }
  };

  const fetchTeachers = async () => {
    try {
      const { data } = await api.get('/admin/teachers?limit=100');
      setTeachers(data.teachers);
    } catch {}
  };

  const handleCheckIn = async () => {
    if (!selectedTeacher) { toast.error('Select a teacher'); return; }
    try { await api.post('/admin/attendance/teacher/checkin', { teacherId: selectedTeacher }); toast.success('Check-in recorded'); fetchAttendance(); }
    catch { toast.error('Check-in failed'); }
  };

  const handleCheckOut = async () => {
    if (!selectedTeacher) { toast.error('Select a teacher'); return; }
    try { await api.post('/admin/attendance/teacher/checkout', { teacherId: selectedTeacher }); toast.success('Check-out recorded'); fetchAttendance(); }
    catch { toast.error('Check-out failed'); }
  };

  const handleCalculateSalary = async () => {
    if (!selectedTeacher || !salaryMonth) return;
    try { const { data } = await api.get(`/admin/attendance/salary/${selectedTeacher}/${salaryMonth}`); setSalaryResult(data); }
    catch { toast.error('Salary calculation failed'); }
  };

  return (
    <>
      <style>{style}</style>
      <div className="att-root att-fade">

        <div className="att-page-header">
          <div>
            <h1 className="att-page-title">Attendance</h1>
            <p className="att-page-sub">Track & manage student and teacher attendance records.</p>
          </div>
        </div>

        {/* Filters */}
        <div className="att-panel">
          <div className="att-panel-header">
            <div className="att-panel-icon blue"><FunnelIcon /></div>
            <div>
              <div className="att-panel-title">Filters</div>
              <div className="att-panel-sub">Select date range and role to view records</div>
            </div>
          </div>
          <div className="att-panel-body">
            <div className="filter-row">
              <div className="filter-field">
                <label className="filter-label">Start Date</label>
                <DatePicker selected={startDate} onChange={date => setStartDate(date)} className="filter-input" />
              </div>
              <div className="filter-field">
                <label className="filter-label">End Date</label>
                <DatePicker selected={endDate} onChange={date => setEndDate(date)} className="filter-input" />
              </div>
              <div className="filter-field">
                <label className="filter-label">Role</label>
                <div className="role-toggle">
                  <button className={`role-btn${role==='student'?' active':''}`} onClick={() => setRole('student')}>Student</button>
                  <button className={`role-btn${role==='teacher'?' active':''}`} onClick={() => setRole('teacher')}>Teacher</button>
                </div>
              </div>
              <button className="btn btn-blue" onClick={fetchAttendance}>
                <CalendarDaysIcon style={{width:15,height:15,strokeWidth:2}} />Apply Filter
              </button>
            </div>
          </div>
        </div>

        {/* Teacher check-in/out */}
        {role === 'teacher' && (
          <div className="att-panel">
            <div className="att-panel-header">
              <div className="att-panel-icon green"><ClockIcon /></div>
              <div>
                <div className="att-panel-title">Teacher Check-in / Check-out</div>
                <div className="att-panel-sub">Manually record teacher attendance for today</div>
              </div>
            </div>
            <div className="att-panel-body">
              <div className="checkin-grid">
                <div className="filter-field">
                  <label className="filter-label">Select Teacher</label>
                  <select className="filter-select" value={selectedTeacher} onChange={e => setSelectedTeacher(e.target.value)}>
                    <option value="">— Choose teacher —</option>
                    {teachers.map(t => <option key={t._id} value={t.user._id}>{t.user.name}</option>)}
                  </select>
                </div>
                <button className="btn btn-green" onClick={handleCheckIn}>
                  <ArrowRightOnRectangleIcon style={{width:15,height:15,strokeWidth:2}} />Check In
                </button>
                <button className="btn btn-amber" onClick={handleCheckOut}>
                  <ArrowLeftOnRectangleIcon style={{width:15,height:15,strokeWidth:2}} />Check Out
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="att-panel">
          <div className="att-panel-header">
            <div className="att-panel-icon blue"><CalendarDaysIcon /></div>
            <div>
              <div className="att-panel-title">Attendance Records</div>
              <div className="att-panel-sub">{attendances.length} record{attendances.length !== 1 ? 's' : ''} found</div>
            </div>
          </div>
          {attendances.length === 0 ? (
            <div className="att-empty">
              <CalendarDaysIcon style={{width:40,height:40,margin:'0 auto 12px',opacity:.3,display:'block'}} />
              No attendance records found for this range.
            </div>
          ) : (
            <>
              <div className="att-table-wrap">
                <table className="att-table">
                  <thead>
                    <tr>
                      <th>User</th><th>Date</th><th>Status</th>
                      <th>Check In</th><th>Check Out</th>
                      <th>Work Hours</th><th>Late (min)</th><th>Early Exit (min)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendances.map(att => (
                      <tr key={att._id}>
                        <td><div className="user-cell"><div className="user-avatar-sm">{getInitials(att.user?.name)}</div>{att.user?.name}</div></td>
                        <td className="mono">{new Date(att.date).toLocaleDateString('en-US',{day:'2-digit',month:'short',year:'numeric'})}</td>
                        <td><StatusBadge status={att.status} /></td>
                        <td className="mono">{att.checkIn ? new Date(att.checkIn).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'}) : '—'}</td>
                        <td className="mono">{att.checkOut ? new Date(att.checkOut).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'}) : '—'}</td>
                        <td className="mono">{att.workHours != null ? Number(att.workHours).toFixed(2) : '—'}</td>
                        <td className="mono" style={{color:att.lateMinutes>0?'#f59e0b':undefined}}>{att.lateMinutes ?? '—'}</td>
                        <td className="mono" style={{color:att.earlyExitMinutes>0?'#ef4444':undefined}}>{att.earlyExitMinutes ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Mobile cards */}
              <div className="att-cards">
                {attendances.map(att => (
                  <div key={att._id} className="att-card">
                    <div className="att-card-top">
                      <div className="user-cell"><div className="user-avatar-sm">{getInitials(att.user?.name)}</div>{att.user?.name}</div>
                      <StatusBadge status={att.status} />
                    </div>
                    <div className="att-card-row"><span className="att-card-lbl">Date</span><span className="mono">{new Date(att.date).toLocaleDateString('en-US',{day:'2-digit',month:'short',year:'numeric'})}</span></div>
                    <div className="att-card-row"><span className="att-card-lbl">Check In</span><span className="mono">{att.checkIn ? new Date(att.checkIn).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'}) : '—'}</span></div>
                    <div className="att-card-row"><span className="att-card-lbl">Check Out</span><span className="mono">{att.checkOut ? new Date(att.checkOut).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'}) : '—'}</span></div>
                    <div className="att-card-row"><span className="att-card-lbl">Work Hours</span><span className="mono">{att.workHours != null ? Number(att.workHours).toFixed(2) : '—'}</span></div>
                    {att.lateMinutes>0        && <div className="att-card-row"><span className="att-card-lbl">Late</span><span className="mono" style={{color:'#f59e0b'}}>{att.lateMinutes} min</span></div>}
                    {att.earlyExitMinutes>0   && <div className="att-card-row"><span className="att-card-lbl">Early Exit</span><span className="mono" style={{color:'#ef4444'}}>{att.earlyExitMinutes} min</span></div>}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Salary calculator */}
        <div className="att-panel">
          <div className="att-panel-header">
            <div className="att-panel-icon purple"><BanknotesIcon /></div>
            <div>
              <div className="att-panel-title">Salary Calculator</div>
              <div className="att-panel-sub">Calculate net salary based on attendance and deductions</div>
            </div>
          </div>
          <div className="att-panel-body">
            <div className="filter-row">
              <div className="filter-field">
                <label className="filter-label">Teacher</label>
                <select className="filter-select" value={selectedTeacher} onChange={e => setSelectedTeacher(e.target.value)}>
                  <option value="">— Choose teacher —</option>
                  {teachers.map(t => <option key={t._id} value={t.user._id}>{t.user.name}</option>)}
                </select>
              </div>
              <div className="filter-field">
                <label className="filter-label">Month</label>
                <input type="month" className="filter-input" value={salaryMonth} onChange={e => setSalaryMonth(e.target.value)} />
              </div>
              <button className="btn btn-purple" onClick={handleCalculateSalary}>
                <BanknotesIcon style={{width:15,height:15,strokeWidth:2}} />Calculate Salary
              </button>
            </div>

            {salaryResult && (
              <div className="salary-card">
                <div className="salary-item"><span className="salary-item-label">Teacher</span><span className="salary-item-value">{salaryResult.teacher}</span></div>
                <div className="salary-item"><span className="salary-item-label">Month</span><span className="salary-item-value">{salaryResult.month}</span></div>
                <div className="salary-item"><span className="salary-item-label">Total Hours</span><span className="salary-item-value">{salaryResult.totalHours?.toFixed(2)} hrs</span></div>
                <div className="salary-divider" />
                <div className="salary-item"><span className="salary-item-label">Late Minutes</span><span className="salary-item-value" style={{color:'#f59e0b'}}>{salaryResult.totalLateMinutes} min</span></div>
                <div className="salary-item"><span className="salary-item-label">Early Exit</span><span className="salary-item-value" style={{color:'#f59e0b'}}>{salaryResult.totalEarlyMinutes} min</span></div>
                <div className="salary-item"><span className="salary-item-label">Base Salary</span><span className="salary-item-value">Rs. {salaryResult.baseSalary?.toFixed(2)}</span></div>
                <div className="salary-item"><span className="salary-item-label">Deductions</span><span className="salary-item-value danger">− Rs. {salaryResult.deductions?.toFixed(2)}</span></div>
                <div className="salary-divider" />
                <div className="salary-item"><span className="salary-item-label">Net Salary</span><span className="salary-item-value highlight">Rs. {salaryResult.netSalary?.toFixed(2)}</span></div>
              </div>
            )}
          </div>
        </div>

      </div>
    </>
  );
};

export default Attendance;